import { defineStore } from 'pinia'
import axios from 'axios'
import {
  buildBrowserSuccessUrl,
  clearBrowserLoginState,
  loadBrowserLoginState,
  pollBrowserSession,
  saveBrowserLoginState,
} from '../auth/browser-session.mjs'

const DEFAULT_ACCOUNT_PORTAL_URL = 'https://account.autosquare.org.cn'

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null,
    accountPortalUrl: DEFAULT_ACCOUNT_PORTAL_URL,
    initialized: false,
    profileVerified: false,
    authVersion: 0,
    loggingOut: false,
    profileRequest: null,
    loginRequest: null,
    configRequest: null
  }),
  getters: {
    isAuthenticated: state => Boolean(state.user),
    isAdmin: state => ['admin', 'superuser'].includes(state.user?.role)
  },
  actions: {
    setUser(user) {
      this.user = user
      this.profileVerified = Boolean(user)
      this.initialized = true
    },
    clearAuth() {
      this.authVersion++
      this.user = null
      this.profileVerified = false
      this.initialized = true
      this.profileRequest = null
      this.loginRequest = null

    },
    notifySessionChange() {
      try {
        window.localStorage.setItem('autosquare_website_session_changed', String(Date.now()) + ':' + Math.random())
      } catch { /* 禁用本地存储时，仍通过页面恢复事件校验会话。 */ }
    },
    async initializeAuth() {
      if (this.initialized) return this.user
      await this.fetchAuthConfig()
      return this.fetchProfile()
    },
    async ensureProfile() {
      await this.fetchAuthConfig()
      return this.fetchProfile()
    },
    async fetchAuthConfig() {
      if (this.configRequest) return this.configRequest
      const pending = axios.get('/api/auth/config/')
        .then(response => {
          const portal = response.data?.account_portal_url
          if (typeof portal === 'string' && portal.trim()) {
            this.accountPortalUrl = portal.trim().replace(/\/$/, '')
          }
          return this.accountPortalUrl
        })
        .finally(() => { if (this.configRequest === pending) this.configRequest = null })
      this.configRequest = pending
      return pending
    },
    async fetchProfile() {
      if (this.loggingOut) return null
      if (this.profileRequest) return this.profileRequest
      const version = this.authVersion
      const pending = axios.get('/api/auth/profile/')
        .then(response => {
          if (version !== this.authVersion || this.loggingOut) return null
          this.setUser(response.data)
          return this.user
        })
        .catch(error => {
          if (version !== this.authVersion || this.loggingOut) return null
          if (error.response?.status === 401) {
            this.setUser(null)
            return null
          }
          this.profileVerified = false
          throw error
        })
        .finally(() => { if (this.profileRequest === pending) this.profileRequest = null })
      this.profileRequest = pending
      return pending
    },
    async beginBrowserLogin(returnPath = '/') {
      const before = this.authVersion
      await this.fetchAuthConfig()
      if (before !== this.authVersion || this.loggingOut) throw new Error('该登录操作已被取消。')
      const current = await this.fetchProfile()
      if (before !== this.authVersion || this.loggingOut) throw new Error('该登录操作已被取消。')
      if (current) return { return_path: returnPath }
      const version = ++this.authVersion
      this.profileRequest = null
      const created = await axios.post('/api/auth/browser-session/', {

        success_url: buildBrowserSuccessUrl(window.location)
      })
      if (version !== this.authVersion || this.loggingOut) throw new Error('该登录操作已被取消。')
      const session = created.data
      saveBrowserLoginState(window.sessionStorage, session, returnPath)
      window.location.replace(session.login_url)
    },
    async completeBrowserLogin() {
      if (this.loginRequest) return this.loginRequest
      const pending = this.finishBrowserLogin()
        .finally(() => { if (this.loginRequest === pending) this.loginRequest = null })
      this.loginRequest = pending
      return pending
    },
    async finishBrowserLogin() {
      const version = this.authVersion
      const session = loadBrowserLoginState(window.sessionStorage)
      const current = await this.fetchProfile()
      if (version !== this.authVersion || this.loggingOut) throw new Error('该登录操作已被取消。')
      if (current) {
        clearBrowserLoginState(window.sessionStorage)
        return session?.return_path || '/'
      }
      if (!session) throw new Error('未找到待完成的登录会话，请重新登录。')
      let result
      try {
        result = await pollBrowserSession({ api: axios, session,
          isCurrent: () => version === this.authVersion && !this.loggingOut })
      } catch (error) {
        if (error.sessionExpired && version === this.authVersion) clearBrowserLoginState(window.sessionStorage)
        throw error
      }
      if (version !== this.authVersion || this.loggingOut) throw new Error('该登录操作已被取消。')
      clearBrowserLoginState(window.sessionStorage)
      // 登录完成后，领取期间发出的旧资料请求不能再写入身份。
      this.authVersion++
      this.profileRequest = null
      this.setUser(result.user || null)
      this.notifySessionChange()

      return session.return_path
    },
    async logout() {
      if (this.loggingOut) return
      this.loggingOut = true
      this.authVersion++
      this.profileRequest = null
      this.loginRequest = null
      try {
        await axios.post('/api/auth/logout/')
        this.clearAuth()
        clearBrowserLoginState(window.sessionStorage)
        this.notifySessionChange()
      } finally {
        this.loggingOut = false
      }
    }
  }
})
